import React, { useState } from 'react';

const FAQ = () => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (id) => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const FAQItem = ({ id, question, children }) => (
    <div className="faq-item">
      <div 
        className={`faq-question ${openItems[id] ? 'active' : ''}`}
        onClick={() => toggleItem(id)}
      >
        <h3>{question}</h3>
        <i className={`fa-solid fa-chevron-down faq-icon ${openItems[id] ? 'rotated' : ''}`}></i>
      </div>
      <div className={`faq-answer ${openItems[id] ? 'open' : ''}`}>
        <div className="faq-content">
          {children}
        </div>
      </div>
    </div>
  );

  return (
    <div className="doc-content">
      <div className="quickstart-header">
        <p className="quickstart-category">Introduction</p>
        <h1>Frequently Asked Questions</h1>
      </div>
      
      <div className="info-banner">
        <i className="fa-solid fa-question-circle"></i>
        <div>
          <p>Common questions about MemU's memory framework, implementation, and use cases. Find quick answers to help you get started.</p>
        </div>
      </div>
      
      <FAQItem id="what-is-memu" question="What is MemU?">
        <p>MemU is an open-source memory framework designed for AI applications. It provides intelligent memory management with 92.9% accuracy and up to 90% cost reduction compared to traditional memory solutions. Unlike simple vector databases or RAG systems, MemU offers autonomous memory organization, self-evolution, and intelligent forgetting mechanisms.</p>
      </FAQItem>
      
      <FAQItem id="memu-vs-rag" question="How is MemU different from RAG?">
        <p>While RAG (Retrieval-Augmented Generation) is excellent for retrieving facts from documents, it's stateless and doesn't learn about users over time. MemU provides continuity by storing decisions, preferences, and context—not just knowledge. MemU and RAG are complementary: RAG informs the LLM, while MemU shapes its memory.</p>
      </FAQItem>
      
      <FAQItem id="memory-system-unique" question="What makes MemU's memory system unique?">
        <p>MemU features several unique capabilities:</p>
        <ul>
          <li><strong>File system approach:</strong> Organizes memories as documents/videos/images rather than fragmented data</li>
          <li><strong>Agent-based management:</strong> Uses an AI agent instead of simple LLM inference</li>
          <li><strong>Dual retrieval:</strong> Combines embedding search with document search</li>
          <li><strong>Self-evolution:</strong> Continuously improves and generates new insights</li>
          <li><strong>Intelligent forgetting:</strong> Gracefully manages memory decay and importance</li>
        </ul>
      </FAQItem>
      
      <FAQItem id="autonomous-memory" question="How does the autonomous memory management work?">
        <p>MemU uses a dedicated memory agent that automatically decides what to record, modify, or archive based on relevance and usage patterns. Think of it as having a personal librarian who organizes your thoughts without manual intervention. The system uses LLM-powered analysis, pattern recognition, and usage analytics to maintain optimal memory organization.</p>
      </FAQItem>
      
      <FAQItem id="memory-retrieval-speed" question="How fast is memory retrieval?">
        <p>MemU provides sub-50ms memory lookups at scale. The system uses intelligent indexing, tiered storage (hot/warm/cool/cold), and dynamic importance algorithms to ensure that the most relevant memories are instantly accessible while maintaining high performance regardless of memory size.</p>
      </FAQItem>
      
      <FAQItem id="forgetting-mechanism" question="How does the forgetting mechanism work?">
        <p>Unlike traditional systems that either keep everything or delete arbitrarily, MemU implements human-like forgetting. Less important memories gradually become less accessible rather than disappearing instantly. The system preserves core context while allowing details to fade, and "forgotten" memories can be recovered if they become relevant again.</p>
      </FAQItem>
      
      <FAQItem id="integration" question="How do I integrate MemU into my existing project?">
        <p>MemU is designed to plug into your existing agent framework without replacing your LLM or workflows. It adds an intelligent memory layer on top. You can start with our managed cloud platform for instant access or use the open-source SDK for self-hosted deployments. Check our Quickstart guide for detailed integration steps.</p>
      </FAQItem>
      
      <FAQItem id="deployment-options" question="What deployment options are available?">
        <p>MemU offers flexible deployment options:</p>
        <ul>
          <li><strong>Cloud Platform:</strong> Managed service with instant access and scalability</li>
          <li><strong>Self-Hosted:</strong> Open-source SDK for complete control and privacy</li>
          <li><strong>Enterprise Edition:</strong> Dedicated infrastructure with enterprise features</li>
          <li><strong>Hybrid:</strong> Combine cloud and on-premise components as needed</li>
        </ul>
      </FAQItem>
      
      <FAQItem id="production-ready" question="Is MemU suitable for production applications?">
        <p>Yes, MemU is built for production use with enterprise-grade reliability and performance. It includes observability dashboards, APIs for fine-grained control, automatic scaling, and comprehensive monitoring. The system has been tested at scale and provides consistent performance as memory grows.</p>
      </FAQItem>
      
      <FAQItem id="main-use-cases" question="What are the main use cases for MemU?">
        <p>MemU is ideal for applications requiring persistent, evolving memory:</p>
        <ul>
          <li><strong>AI Education:</strong> Personalized learning with memory retention</li>
          <li><strong>AI Therapy:</strong> Mental health support with context awareness</li>
          <li><strong>AI Roleplay:</strong> Interactive storytelling and gaming</li>
          <li><strong>Customer Support:</strong> Context-aware assistance across sessions</li>
          <li><strong>Content Creation:</strong> Creative applications with memory-enhanced generation</li>
        </ul>
      </FAQItem>
      
      <FAQItem id="multimodal-content" question="Can MemU handle multimodal content?">
        <p>Yes, MemU supports multimodal memory including text, images, videos, and other file types. The system can create connections between different media types and maintain context across various content formats, making it ideal for rich, multimedia AI applications.</p>
      </FAQItem>
      
      <FAQItem id="cost-savings" question="How much can I save with MemU?">
        <p>MemU can reduce operational costs by up to 90% through intelligent memory management. This includes token usage reduction (smart context injection), storage optimization (automatic compression and archival), and processing efficiency (focus on important memories). The exact savings depend on your specific use case and memory patterns.</p>
      </FAQItem>
      
      <FAQItem id="scalability" question="How does MemU scale with growing memory?">
        <p>MemU is designed for scalability. The autonomous organization system improves as data volume grows, maintaining consistent performance through intelligent indexing, tiered storage, and automatic memory management. The system can handle millions of memories while preserving sub-50ms retrieval times.</p>
      </FAQItem>
      
      <FAQItem id="support-channels" question="Where can I get support?">
        <p>We provide multiple support channels:</p>
        <ul>
          <li><strong>Documentation:</strong> Comprehensive guides and API references</li>
          <li><strong>Community:</strong> GitHub discussions and community forums</li>
          <li><strong>Discord:</strong> Real-time chat with developers and users</li>
          <li><strong>Enterprise Support:</strong> Dedicated support for enterprise customers</li>
        </ul>
      </FAQItem>
      
      <FAQItem id="contribute" question="How can I contribute to MemU?">
        <p>MemU is open-source and welcomes contributions! You can contribute through:</p>
        <ul>
          <li>Code contributions via GitHub pull requests</li>
          <li>Bug reports and feature requests</li>
          <li>Documentation improvements</li>
          <li>Community support and discussions</li>
          <li>Sharing use cases and success stories</li>
        </ul>
      </FAQItem>
      
      <div className="platform-section">
        <h2>Still have questions?</h2>
        <p>If you couldn't find the answer you're looking for, feel free to reach out:</p>
        <div className="platform-cards">
          <div className="platform-card">
            <div className="card-icon">
              <i className="fa-brands fa-github"></i>
            </div>
            <h3>GitHub Discussions</h3>
            <p>Ask technical questions and get help from the community</p>
          </div>
          
          <div className="platform-card">
            <div className="card-icon">
              <i className="fa-brands fa-discord"></i>
            </div>
            <h3>Discord Community</h3>
            <p>Real-time chat with developers and other users</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;